import { JobFeatureCollection } from "../../../services/axios/jobs";
import {
  ArrowData,
  BoundaryData,
  HazardMarkerType,
  LabelMarkerType,
} from "../contexts/DataContext";
import {
  ArrowFeature,
  BoundaryFeature,
  HazardFeature,
  LabelFeature,
  SiteMapSurfaceType,
  SurfaceFeature,
} from "../DataAndMap";
import { COLOR_BLACK, COLOR_BLUE, COLOR_RED } from "./colors";

type ProcessedFeatures = {
  surfaceFeatures: SiteMapSurfaceType[];
  hazardMarkers: HazardMarkerType[];
  labelMarkers: LabelMarkerType[];
  arrows: ArrowData[];
  boundary?: BoundaryData;
};

export const processFeatureCollection = (
  featureCollection?: JobFeatureCollection,
): ProcessedFeatures => {
  const surfaceFeatures: SiteMapSurfaceType[] = [];
  const hazardMarkers: HazardMarkerType[] = [];
  const labelMarkers: LabelMarkerType[] = [];
  const arrows: ArrowData[] = [];
  let boundary: BoundaryData | undefined = undefined;

  if (!featureCollection || !featureCollection.features) {
    return {
      surfaceFeatures,
      hazardMarkers,
      labelMarkers,
      arrows,
      boundary,
    };
  }

  let i = 1; // MUST NOT START AT ZERO!  There is a bug in Google's APIs with removing features with id 0.
  featureCollection.features.forEach((feature) => {
    let newFeature;
    switch (feature.properties.type) {
      case "surface":
        newFeature = {
          id: i,
          type: feature.geometry.type,
          coordinates: (feature as SurfaceFeature).geometry.coordinates.map(
            (ring) =>
              ring.map((coord) => ({
                lat: coord[1],
                lng: coord[0],
              })),
          ),
          properties: {
            surfaceType: feature.properties.surfaceType,
          },
        } as SiteMapSurfaceType;

        surfaceFeatures.push(newFeature);
        break;
      case "hazard":
        newFeature = {
          id: i,
          lat: (feature as HazardFeature).geometry.coordinates[1],
          lng: (feature as HazardFeature).geometry.coordinates[0],
          color:
            feature.properties.color === COLOR_RED.name
              ? COLOR_RED.name
              : feature.properties.color === COLOR_BLACK.name
                ? COLOR_BLACK.name
                : COLOR_BLUE.name,
        } as HazardMarkerType;

        hazardMarkers.push(newFeature);
        break;
      case "label":
        newFeature = {
          id: i,
          lat: (feature as LabelFeature).geometry.coordinates[1],
          lng: (feature as LabelFeature).geometry.coordinates[0],
          text: feature.properties.text,
          color: feature.properties.color,
          size: feature.properties.size,
        } as LabelMarkerType;

        labelMarkers.push(newFeature);
        break;
      case "arrow":
        newFeature = {
          id: i,
          coords: (feature as ArrowFeature).geometry.coordinates.map(
            (coord) => [coord[1], coord[0]],
          ),
          color:
            feature.properties.color === COLOR_RED.name
              ? COLOR_RED.name
              : feature.properties.color === COLOR_BLACK.name
                ? COLOR_BLACK.name
                : COLOR_BLUE.name,
        } as ArrowData;

        arrows.push(newFeature);
        break;
      case "boundary":
        boundary = {
          id: 1,
          coords: (feature as BoundaryFeature).geometry.coordinates.map(
            (coord) => [coord[1], coord[0]],
          ),
          color: COLOR_RED.name,
        } as BoundaryData;
        break;
      default:
        break;
    }

    i++;
  });

  return {
    boundary,
    surfaceFeatures,
    hazardMarkers,
    labelMarkers,
    arrows,
  };
};

export const produceFeatureCollection = (
  surfaceFeatures: SiteMapSurfaceType[],
  hazardMarkers: HazardMarkerType[],
  labelMarkers: LabelMarkerType[],
  arrows: ArrowData[],
  boundary?: BoundaryData,
): JobFeatureCollection => {
  const featureCollection: JobFeatureCollection = {
    type: "FeatureCollection",
    features: [],
  };

  if (boundary) {
    const boundaryFeature: BoundaryFeature = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: boundary.coords.map((coord) => [coord[1], coord[0]]),
      },
      properties: {
        color: boundary.color,
        type: "boundary",
      },
    };
    featureCollection.features.push(boundaryFeature);
  }

  surfaceFeatures.forEach((feature) => {
    const newSurfaceFeature: SurfaceFeature = {
      type: "Feature",
      geometry: {
        type: "Polygon",
        coordinates: feature.coordinates.map((ring) =>
          ring.map((coord) => [coord.lng, coord.lat]),
        ),
      },
      properties: {
        surfaceType: feature.properties.surfaceType,
        type: "surface",
      },
    };
    featureCollection.features.push(newSurfaceFeature);
  });

  hazardMarkers.forEach((marker) => {
    const newHazardFeature: HazardFeature = {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [marker.lng, marker.lat],
      },
      properties: {
        color: marker.color,
        type: "hazard",
      },
    };
    featureCollection.features.push(newHazardFeature);
  });

  labelMarkers.forEach((marker) => {
    const newLabelFeature: LabelFeature = {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [marker.lng, marker.lat],
      },
      properties: {
        color: marker.color,
        size: marker.size,
        text: marker.text,
        type: "label",
      },
    };
    featureCollection.features.push(newLabelFeature);
  });

  arrows.forEach((arrow) => {
    const newArrowFeature: ArrowFeature = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: arrow.coords.map((coord) => [coord[1], coord[0]]),
      },
      properties: {
        color: arrow.color,
        type: "arrow",
      },
    };
    featureCollection.features.push(newArrowFeature);
  });

  return featureCollection;
};

const normalizeRing = (ring: number[][]): number[][] => {
  if (ring.length > 1) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (
      Math.abs(first[0] - last[0]) < 0.0000001 &&
      Math.abs(first[1] - last[1]) < 0.0000001
    ) {
      return ring.slice(0, -1);
    }
  }
  return ring;
};

export const normalizeFeatureCollection = (
  fc?: JobFeatureCollection | null,
): JobFeatureCollection | null | undefined => {
  if (!fc || !Array.isArray(fc.features)) return fc;

  return {
    ...fc,
    features: fc.features.map((feature) => {
      if (
        feature &&
        feature.properties.type === "surface" &&
        feature.geometry.type === "Polygon"
      ) {
        return {
          ...feature,
          geometry: {
            ...feature.geometry,
            coordinates: feature.geometry.coordinates.map((ring) =>
              normalizeRing(ring),
            ),
          },
        } as SurfaceFeature;
      }
      return feature;
    }),
  };
};

export const areFeatureCollectionsEqual = (
  a?: JobFeatureCollection | null,
  b?: JobFeatureCollection | null,
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    JSON.stringify(normalizeFeatureCollection(a)) ===
    JSON.stringify(normalizeFeatureCollection(b))
  );
};
